import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Icon } from "@iconify/react";
import { useCart } from "../context/CartContext";
import "./Navbar/Navbar.css";

const Navbar = ({ userData, adminData, handleUserLogout, handleAdminLogout }) => {
  const [scrolled, setScrolled]         = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const dropdownRef = React.useRef(null);
  const { cartCount, toggleCart } = useCart();
  const navigate = useNavigate();

  // Sticky-scroll effect
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close on ESC
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setDropdownOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".user-dropdown")) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleDropdown = () => {
    setDropdownOpen((p) => !p);
  };
  const closeDropdown  = () => {
    setDropdownOpen(false);
  };

  /* ─── JSX ─────────────────────────────── */
  return (
    <>
      <motion.header
        className={`navbar-wrapper ${scrolled ? "scrolled" : ""}`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="navbar-container">

          {/* Brand */}
          <Link to="/" className="nav-brand">
            <Icon icon="mdi:food-turkey" className="brand-icon" />
            <span>FoodExpress</span>
          </Link>

          {/* Right-side actions */}
          <div className="nav-actions">

            <Link to="/menu" className="nav-action-link">
              <Icon icon="mdi:silverware-fork-knife" />
              <span>Menu</span>
            </Link>

            {/* Cart */}
            <button className="cart-btn" onClick={toggleCart} aria-label="Open cart">
              <Icon icon="mdi:cart-outline" />
              <AnimatePresence>
                {cartCount > 0 && (
                  <motion.span
                    className="cart-badge"
                    key={cartCount}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                  >
                    {cartCount}
                  </motion.span>
                )}
              </AnimatePresence>
            </button>

            {/* ── Logged-in user ── */}
            {userData ? (
              <div className="user-dropdown" ref={dropdownRef} onMouseDown={(e) => e.stopPropagation()}>

                {/* Avatar button */}
                <button
                  className="user-btn"
                  onClick={toggleDropdown}
                  aria-label="Open profile menu"
                  aria-expanded={dropdownOpen}
                >
                  <span className="user-avatar-letter">
                    {userData.username ? userData.username.charAt(0).toUpperCase() : "U"}
                  </span>
                </button>

                {/* Dropdown panel */}
                <div className={`dropdown-menu ${dropdownOpen ? 'dropdown-menu--open' : ''}`}>
                  <div className="dropdown-header">
                    <div className="dropdown-avatar">
                      {userData.username ? userData.username.charAt(0).toUpperCase() : "U"}
                    </div>
                    <div className="dropdown-user-info">
                      <p className="dropdown-name">{userData.username}</p>
                      <p className="dropdown-email">{userData.email}</p>
                    </div>
                  </div>

                  <div className="dropdown-body">
                    <Link
                      to="/profile"
                      className="dropdown-item"
                      onClick={closeDropdown}
                    >
                      <Icon icon="mdi:account-circle-outline" width="18" />
                      My Profile
                    </Link>

                    <Link
                      to="/orders"
                      className="dropdown-item"
                      onClick={closeDropdown}
                    >
                      <Icon icon="mdi:receipt-text-outline" width="18" />
                      My Orders
                    </Link>

                    <div className="dropdown-divider" />

                    <button
                      className="dropdown-item dropdown-logout"
                      onClick={() => {
                        handleUserLogout();
                        closeDropdown();
                        navigate("/");
                      }}
                    >
                      <Icon icon="mdi:logout" width="18" />
                      Logout
                    </button>
                  </div>
                </div>
              </div>

            /* ── Logged-in admin ── */
            ) : adminData ? (
              <div className="user-dropdown" ref={dropdownRef} onMouseDown={(e) => e.stopPropagation()}>

                <button
                  className="user-btn admin-btn"
                  onClick={toggleDropdown}
                  aria-label="Open admin menu"
                  aria-expanded={dropdownOpen}
                >
                  <Icon icon="mdi:shield-account" width="22" />
                </button>

                <div className={`dropdown-menu ${dropdownOpen ? 'dropdown-menu--open' : ''}`}>
                  <div className="dropdown-header">
                    <div className="dropdown-avatar admin-avatar">
                      <Icon icon="mdi:shield-account" width="22" />
                    </div>
                    <div className="dropdown-user-info">
                      <p className="dropdown-name">{adminData.username || "Admin"}</p>
                      <p className="dropdown-email">Administrator</p>
                    </div>
                  </div>

                  <div className="dropdown-body">
                    <Link
                      to="/admin"
                      className="dropdown-item"
                      onClick={closeDropdown}
                    >
                      <Icon icon="mdi:view-dashboard-outline" width="18" />
                      Admin Dashboard
                    </Link>

                    <div className="dropdown-divider" />

                    <button
                      className="dropdown-item dropdown-logout"
                      onClick={() => {
                        handleAdminLogout();
                        closeDropdown();
                        navigate("/");
                      }}
                    >
                      <Icon icon="mdi:logout" width="18" />
                      Logout Admin
                    </button>
                  </div>
                </div>
              </div>

            /* ── Guest ── */
            ) : (
              <Link to="/login" className="btn-primary">Sign In</Link>
            )}

            {/* Hamburger (mobile) */}
            <button className="hamburger-btn" onClick={() => setMobileMenuOpen(true)} aria-label="Open menu">
              <Icon icon="mdi:menu" />
            </button>
          </div>
        </div>
      </motion.header>

      {/* ── Mobile Drawer ── */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              className="mobile-drawer-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileMenuOpen(false)}
            />
            <motion.div
              className="mobile-drawer"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", bounce: 0, duration: 0.4 }}
            >
              <button className="drawer-close" onClick={() => setMobileMenuOpen(false)} aria-label="Close menu">
                <Icon icon="mdi:close" />
              </button>

              <div className="drawer-links">
                <button
                  onClick={() => { setMobileMenuOpen(false); toggleCart(); }}
                  className="nav-link"
                  style={{ background: "none", border: "none", textAlign: "left", padding: "13px 8px", cursor: "pointer", fontWeight: 500 }}
                >
                  Cart ({cartCount})
                </button>
                <Link to="/menu" onClick={() => setMobileMenuOpen(false)} className="nav-link">Menu</Link>

                {userData ? (
                  <>
                    <Link to="/profile" onClick={() => setMobileMenuOpen(false)} className="nav-link">My Profile</Link>
                    <Link to="/orders"  onClick={() => setMobileMenuOpen(false)} className="nav-link">My Orders</Link>
                    <button
                      onClick={() => { handleUserLogout(); setMobileMenuOpen(false); navigate("/"); }}
                      className="btn-outline mt-4"
                    >
                      Logout
                    </button>
                  </>
                ) : adminData ? (
                  <>
                    <Link to="/admin" onClick={() => setMobileMenuOpen(false)} className="nav-link">Admin Dashboard</Link>
                    <button
                      onClick={() => { handleAdminLogout(); setMobileMenuOpen(false); navigate("/"); }}
                      className="btn-outline mt-4"
                    >
                      Logout Admin
                    </button>
                  </>
                ) : (
                  <>
                    <Link to="/login"      onClick={() => setMobileMenuOpen(false)} className="btn-primary mt-4" style={{ textAlign: "center" }}>Sign In</Link>
                    <Link to="/adminlogin" onClick={() => setMobileMenuOpen(false)} className="nav-link"    style={{ textAlign: "center", marginTop: "12px" }}>Admin Login</Link>
                  </>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
